import { Box, Button, CircularProgress, Stack, SvgIcon, Typography, Dialog, DialogContent, Drawer, useMediaQuery } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import Slider from 'react-slick';
import { getTimeDifferenceString } from 'utils/Utils';
import { useNavigate } from 'react-router-dom';
import { getBasicProfileDetails } from 'api/users';
import { useAuth } from 'hooks/useAuthContext';
import { useSnackbar } from 'components/snackbar';
import { saveJobApplication } from 'api/application';
import { useTheme } from '@mui/material/styles';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import TjResumePreview from 'components/resume-formats/TjResumePreview';
import { updateResumeUrl } from 'api/users';

const settings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

/**
 * SelectResumeOptions component
 *
 * @param {function} fetchResumeData - Function to fetch resume data from API
 * @param {function} onSelect - Function to call when a resume is selected
 * @param {object[]} resumeData - Array of resume data
 * @param {object} selectedResume - Currently selected resume
 * @param {function} onClose - Function to close the dialog
 * @param {string} tab - Current tab
 * @param {object} job - Job data
 * @param {number} index - Current index
 * @param {object} answers - Application answers
 * @param {object} chosenResume - Chosen resume
 * @param {object} messages - Messages data
 * @param {object} candidateDetails - Candidate details
 * @param {number} totalExpectedCompensation - Total expected compensation
 * @param {boolean} disableUpdate - Disable update button
 */

const SelectResumeOptions = ({ fetchResumeData, onSelect, resumeData, selectedResume, onClose, tab, job, index, answers,
  chosenResume, messages, candidateDetails,
  totalExpectedCompensation, disableUpdate,
  currentFixed,
  currentVariable,
  currentPerks,
  expectedHourlyRate,
  totalCurrentCTC
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const { setJobApply } = useAuth();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState({});
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [updateResume, setUpdateResume] = useState(false);

  useEffect(() => {

    /**
     * Fetches the candidate's basic profile details from the API and updates the component's state with the profile data.
     * If the API request is successful and contains candidate data, it sets the profile state with the fetched data.
     * If the request fails or does not contain candidate data, it displays an error notification.
     * Logs any errors encountered during the fetch operation and notifies the user of the failure.
     */

    const fetchData = async () => {
      try {
        const profileResponse = await getBasicProfileDetails();
        let profile = {};
        if (profileResponse?.success && profileResponse?.data?.candidate) {
          profile = profileResponse?.data?.candidate || {};
          setProfile(profile);
        } else {
          enqueueSnackbar(profileResponse?.message, { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Failed to fetch data. Please try again later.', { variant: 'error' });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (sliderRef?.current?.innerSlider) {
        sliderRef.current.slickGoTo(index);
        clearInterval(interval);
      } else {
        console.log("Waiting for innerSlider to initialize...");
      }
    }, 100);

    return () => clearInterval(interval);
  }, [index]);


  /**
   * Handles the selection of a resume option.
   * Calls the onSelect callback with the selected resume item and index.
   * @param {object} item - The selected resume item.
   * @param {number} index - The index of the selected resume item.
   */
  const handleSelect = (item, index) => {
    onSelect(item, index);
  };

  /**
   * Handles navigation to the resume creation/update page.
   * Sets the job application context with the selected resume and its index,
   * and navigates to the create/update resume page based on whether the resume
   * has a link or not.
   * @param {Object} item - The selected resume item.
   * @param {number} index - The index of the selected resume item.
   */
  const handleNavigation = (item, index) => {
    setJobApply({
      job, openChatBot: true, resume: selectedResume, answer: answers, tab, messages, candidateDetails,
      totalExpectedCompensation, disableUpdate, chosenResume: item, openSelectResume: true, index,
      partTimeContinueFlag: true, continueFlag: true,
      currentFixed,
      currentVariable,
      currentPerks, hourlyRate: expectedHourlyRate,
      totalCurrentCTC
    });
    const data = item;
    const totalResume = resumeData?.length;
    if (item?.link) navigate('/create-update-resume', { state: { data, totalResume, tab, jobData: job, uploadResume: true } });
    else navigate('/create-update-resume', { state: { data, totalResume, tab, jobData: job } });
    localStorage.setItem('openChatBot', true);
  };

  /**
   * Handles navigation to the resume creation/update page from the select resume options.
   * Sets the job application context with the selected resume and its index, and navigates to the
   * create/update resume page based on whether a 'Master Resume' already exists or not.
   */
  const handleNavigateToResume = () => {
    setJobApply({
      job, openChatBot: true, resume: selectedResume, answer: answers, tab, messages, candidateDetails,
      totalExpectedCompensation, disableUpdate, openSelectResume: true,
      index: resumeData?.length,
      partTimeContinueFlag: true, continueFlag: true,
      currentFixed,
      currentVariable,
      currentPerks, hourlyRate: expectedHourlyRate,
      totalCurrentCTC
    });
    localStorage.setItem('openChatBot', true);
    const isResume = resumeData?.find(item => item.title === 'Master Resume');
    if (!isResume) navigate('/create-update-resume', { state: { isMaster: true } });
    else navigate('/create-update-resume', { state: { totalResume: resumeData?.length, createNewResume: true } });
  };

  /**
   * Handles profile navigation and job saving.
   * If the current tab is '2', it saves the job using handleSaveJob.
   * Then, it navigates the user to the profile page.
   */

  const handleProfile = () => {
    if (tab === '2') handleSaveJob(job);
    navigate('/profile');
  };

  /**
   * Handles saving a job application.
   * Calls the saveJobApplication API to set the job status to 'saved'.
   * If the API call is successful, it updates the application status and displays a success message.
   * If the API call fails, it displays an error message.
   * @param {Object} job - The job to be saved.
   * @returns {Promise<void>}
   */
  const handleSaveJob = async (job) => {
    const response = await saveJobApplication({ jobId: job?._id, companyId: job?.company_id, type: 'saved' });
    if (response?.success && response?.data != null) {
      job.application_saved = response?.data?.status === 'saved';
    } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
    }
  };

  /**
   * Triggers the file input click event to open the file selector dialog.
   * This allows the user to select a resume file to upload.
   */
  const handleUploadResume = () => {
    fileInputRef.current.click()
  };

  /**
   * Handles file input change event.
   * Checks if a file has been selected and if the file size is within the allowed limit.
   * If the file is valid, it calls the updateResumeUrl API to upload the file and update the resume URL.
   * If the API call is successful, it fetches the resume data and updates the component state.
   * If the API call fails, it displays an error message.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The event object from the file input change event.
   * @returns {Promise<void>}
   */
  const handleFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      enqueueSnackbar("No file selected", { variant: "error" });
      return;
    };
    const file = e.target.files[0];
    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar("File size must be less than 2 MB. Please upload a smaller file", { variant: "error" });
      return;
    }
    <input type="file"
      accept=".pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ref={fileInputRef}
      onChange={handleFileChange}
      style={{ display: 'none' }}
    />
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await updateResumeUrl(formData);
      if (response?.success && response?.data) {
        fetchResumeData();
      } else {
        enqueueSnackbar("Failed to update resume", { variant: "error" });
        return;
      }
    }
    catch (error) {
      console.error("Error uploading file:", error);
      enqueueSnackbar("Error uploading file. Please try again.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <Box className="select-resume-options-container">
        <Box className="resume-options-slider-container">
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  };

  return (
    <Box className="select-resumes-dialog select-resume-options-container">
      <input type="file" accept="application/pdf" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
      <Box className="resume-options-slider-container">
        <Slider {...settings} ref={sliderRef}>
          {resumeData &&
            resumeData?.map((item, index) => (
              <Box className="resume-option-item" key={index}
                onClick={(e) => {
                  if (!e.target.classList.contains('resume-option-item')) {
                    return;
                  }
                  onClose();
                }}
              >
                <Box className="header-update-select-resume">
                  <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                    <Box>
                      <Typography className="resume-title">{item?.link ? 'Uploaded Resume' : item?.title}</Typography>
                      <Typography className="last-updated">
                        {(item?.last_updated_at || item?.updatedAt)
                          ? getTimeDifferenceString(item.last_updated_at || item.updatedAt, "Updated") :
                          getTimeDifferenceString(profile?.last_updated_at || profile?.updatedAt, "Updated")}
                      </Typography>
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={2}>
                        <Button variant="outlined" size="small" className="update-btn" onClick={() => item?.link ? handleUploadResume(item) : handleNavigation(item, index)}>
                          {item?.link ? 'Replace' : 'Update'}
                        </Button>
                        <Button variant="contained" size="small" className="select-resume-btn" onClick={() => handleSelect(item, index)}>
                          Select Resume
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                <Box className="resume-options-preview"
                  style={{
                    cursor: 'pointer',
                    border: '1.005px solid var(--Neutral-300, #D2D5DB)',
                    borderRadius: 'var(--Spacing-4x, 12px)',
                    width: '100%'
                  }}>
                  {item?.link ? (
                    <>
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                      <div style={{ width: '100%' }}>
                        <Viewer fileUrl={process.env.REACT_APP_END_POINT + item?.link}
                          defaultScale={.975}
                          style={{ width: '100%' }} />
                      </div>
                    </>
                  ) :
                    <TjResumePreview resume={item} />
                  }
                </Box>
              </Box>
            ))}

          {resumeData?.length < 5 &&
            <Box className="resume-option-item last-slide">
              <Box className="resume-options-preview" sx={{ height: '75vh !important', cursor: 'pointer' }}>
                <Box className="create-new-resume-slide">
                  <Box className="inner-container" onClick={handleNavigateToResume}>
                    <SvgIcon >
                      <svg xmlns="http://www.w3.org/2000/svg" width="74" height="88" viewBox="0 0 74 88" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M8.35059 0.196289C3.93231 0.196289 0.350586 3.77801 0.350586 8.19629V74.1963C0.350586 78.6146 3.93231 82.1963 8.35059 82.1963H41.0889C40.6326 81.5543 40.2038 80.8927 39.8271 80.1963H10.3506C5.93231 80.1963 2.35059 76.6146 2.35059 72.1963V10.1963C2.35059 5.77801 5.93231 2.19629 10.3506 2.19629H52.3506C56.7689 2.19629 60.3506 5.77801 60.3506 10.1963V51.9912C61.0299 52.1154 61.6978 52.2704 62.3506 52.4638V8.19628C62.3506 3.778 58.7689 0.196289 54.3506 0.196289H8.35059ZM15.5127 48.0078V50.0059H47.1904V48.0078H15.5127ZM15.5127 57.8173V55.8193H44.915C44.1405 56.4295 43.418 57.0981 42.7451 57.8173H15.5127ZM25.3252 29.5264V29.7432V35.7705H37.3779V29.5264H35.3818V33.7725H27.3213V29.5264H25.3252ZM27.8389 26.7021C27.8389 24.7727 29.4211 23.1904 31.3506 23.1904C33.2801 23.1904 34.8643 24.7727 34.8643 26.7021C34.8643 28.6316 33.2799 30.2139 31.3506 30.2139C29.4212 30.2139 27.8389 28.6316 27.8389 26.7021ZM32.8662 26.7021C32.8662 25.8509 32.2018 25.1865 31.3506 25.1865C30.4993 25.1865 29.835 25.8509 29.835 26.7021C29.835 27.5534 30.4991 28.2178 31.3506 28.2178C32.2021 28.2178 32.8662 27.5534 32.8662 26.7021ZM31.3506 16.7646C24.3397 16.7646 18.6357 22.4686 18.6357 29.4795C18.6357 36.4904 24.3397 42.1943 31.3506 42.1943C38.3615 42.1943 44.0674 36.4904 44.0674 29.4795C44.0674 22.4686 38.3615 16.7646 31.3506 16.7646ZM31.3506 18.7607C37.2822 18.7607 42.0693 23.5479 42.0693 29.4795C42.0693 35.4111 37.2822 40.1982 31.3506 40.1982C25.419 40.1982 20.6338 35.4111 20.6338 29.4795C20.6338 23.5479 25.419 18.7607 31.3506 18.7607ZM15.5127 65.628V63.6299H38.9795C38.7106 64.2782 38.4842 64.9467 38.2861 65.628H15.5127ZM50.4399 55.5089C52.4767 54.6653 54.6597 54.2313 56.8643 54.2314C59.0687 54.2315 61.2515 54.6658 63.288 55.5095C65.3246 56.3531 67.175 57.5897 68.7337 59.1486C70.2923 60.7074 71.5287 62.558 72.3721 64.5947C73.2156 66.6313 73.6496 68.8142 73.6494 71.0186C73.6492 75.4703 71.8808 79.7395 68.733 82.8873C65.5852 86.035 61.3159 87.8035 56.8643 87.8036C54.6599 87.8038 52.477 87.3699 50.4404 86.5264C48.4037 85.683 46.5531 84.4466 44.9943 82.888C43.4354 81.3294 42.1989 79.4789 41.3552 77.4423C40.5115 75.4058 40.0772 73.223 40.0771 71.0186C40.077 68.814 40.5111 66.631 41.3546 64.5942C42.1982 62.5574 43.4347 60.7068 44.9936 59.1479C46.5524 57.589 48.4031 56.3525 50.4399 55.5089ZM57.8643 70.0165V63.5165H55.8643V70.0165H49.3643V72.0165H55.8643V78.5165H57.8643V72.0165H64.3643V70.0165H57.8643Z" fill="#1575C6" />
                      </svg>
                    </SvgIcon>
                    <Typography>Create new resume and select</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          }
        </Slider>
      </Box >
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={updateResume}
          onClose={() => setUpdateResume(false)}
          className="download-resume-drawer mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
          sx={{ zIndex: 9999 }}
        >
          <Box>
            <Stack justifyContent={'center'}>
              <Box sx={{ textAlign: 'center' }} mb={3}>
                <Typography className="title" mb={1}>Update resume</Typography>
                <Typography className="info-text">This job will be saved for you. After updating your profile, you will be able to view it in the saved jobs section.</Typography>
              </Box>
              <Stack direction={{ xs: 'column-reverse', md: 'row' }} alignItems={'center'} spacing={2.5}>
                <Button fullWidth variant="outlined" size="medium" onClick={() => setUpdateResume(false)}>
                  Cancel
                </Button>
                <Button fullWidth variant="contained" size="medium" sx={{ textTransform: 'none!important' }}
                  onClick={() => handleProfile()}>Continue to update</Button>
              </Stack>
            </Stack>
          </Box>
        </Drawer >
      ) : (
        <Dialog
          open={updateResume}
          onClose={() => setUpdateResume(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="action-dialog-card external-list-dialog"
        ><DialogContent>
            <Box>
              <Stack justifyContent={'center'}>
                <Box sx={{ textAlign: 'center' }} mb={3}>
                  <Typography className="title" mb={1}>Update resume</Typography>
                  <Typography className="info-text">This job will be saved for you. After updating your profile, you will be able to view it in the saved jobs section.</Typography>
                </Box>
                <Stack direction="row" alignItems={'center'} spacing={2.5}>
                  <Button fullWidth variant="outlined" size="medium" onClick={() => setUpdateResume(false)}>
                    Cancel
                  </Button>
                  <Button fullWidth variant="contained" size="medium" sx={{ textTransform: 'none!important' }}
                    onClick={() => handleProfile()}>Continue to update</Button>
                </Stack>
              </Stack>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box >
  );
};

export default SelectResumeOptions;
